/**
 * Client-side translation helpers for listing name + description.
 *
 * Translations are cached on each product document in `translations[lang]`
 * (written by the `translateAds` Cloud Function). This module reads the cache
 * and, on a cache miss, invokes the callable to fetch/store a translation.
 * An in-flight promise cache dedupes concurrent calls for the same id+lang.
 */
import { Firebase } from './config';

export const SUPPORTED_LANGS = ['en', 'fr', 'de', 'es', 'it', 'bg', 'ru', 'uk', 'be', 'zh', 'fa'];

export const LANG_NAMES = {
  en: 'English',
  fr: 'French',
  de: 'German',
  es: 'Spanish',
  it: 'Italian',
  bg: 'Bulgarian',
  ru: 'Russian',
  uk: 'Ukrainian',
  be: 'Belarusian',
  zh: 'Chinese',
  fa: 'Persian',
};

/**
 * Read a cached translation for a product from its already-loaded doc data.
 * Returns { name, description } or null when no cached translation exists.
 */
export function readCachedTranslation(product, lang) {
  if (!product || !lang) return null;
  const cached = product.translations && product.translations[lang];
  if (!cached || (cached.name == null && cached.description == null)) return null;
  return {
    name: cached.name != null ? cached.name : product.name,
    description: cached.description != null ? cached.description : product.description,
  };
}

// In-flight dedup: Map<`${id}:${lang}`, Promise>
const inFlight = new Map();

let callable;
function getCallable() {
  if (!callable) callable = Firebase.functions().httpsCallable('translateAds');
  return callable;
}

/**
 * Ensure translations exist for the given product ids in `lang`.
 * Calls the `translateAds` Cloud Function once per id+lang (deduped in-flight)
 * and returns { [id]: { name, description } }. Never throws: on failure,
 * returns whatever (possibly partial) results are available.
 */
export function ensureTranslations(ids, lang) {
  const validIds = (Array.isArray(ids) ? ids : []).filter(Boolean);
  if (!lang || validIds.length === 0) return Promise.resolve({});

  // Partition into cached-in-memory (resolved) vs. needs a call.
  const result = {};
  const pending = [];
  for (const id of validIds) {
    const key = `${id}:${lang}`;
    if (inFlight.has(key)) {
      pending.push(
        inFlight.get(key).then((res) => {
          if (res && res[id]) result[id] = res[id];
        })
      );
    } else {
      pending.push(id);
    }
  }

  const freshIds = pending.filter((p) => typeof p === 'string');
  let callPromise = Promise.resolve({});
  if (freshIds.length > 0) {
    callPromise = getCallable()({ ids: freshIds, lang })
      .then((res) => {
          const data = (res && res.data) || {};
          // The function surfaces a structured failure reason when no translation
          // could be produced (missing config, upstream error, etc.). Always log
          // it so misconfiguration is visible in DevTools without server logs.
          if (data.error) {
            console.warn(
              `translations: translateAds error [${data.error.code}]: ${data.error.message}`
            );
          }
          return data;
        })
        .catch((err) => {
          // Network/function failure: log and treat as no results so UI keeps original text.
          console.warn('translations: ensureTranslations failed', err?.message || err);
          return {};
        });
    freshIds.forEach((id) => inFlight.set(`${id}:${lang}`, callPromise));
    // Clear in-flight entries shortly after resolution so a later cache miss can retry.
    callPromise.finally(() => {
      freshIds.forEach((id) => {
        if (inFlight.get(`${id}:${lang}`) === callPromise) inFlight.delete(`${id}:${lang}`);
      });
    });
  }

  return Promise.all([
    callPromise.then((res) => {
      Object.assign(result, res);
    }),
    ...pending.filter((p) => typeof p !== 'string'),
  ]).then(() => result);
}
